// BE#313 / FE#313 — decide what a calendar campaign-occurrence click should open.
//
// A follow-up campaign is a DRIP. Editing it must show ALL its steps — exactly like opening it from
// the campaigns LIST, which navigates to `/campaigns/:id` with the DRIP's id (management-list uses
// `card.id = drip._id`). That route resolves BY IDENTITY (campaign-edit-route / SequenceDetail fetch
// both a drip and a campaign for the id): a DRIP id → the full sequence (Message 1 + Wait + Follow-up
// …, each editable); a STEP-CAMPAIGN id → only that one step (Message 1). So a drip occurrence must
// open the **drip id**, NOT the materialized step campaign's `_id` — routing to the step id lands on
// the single-step view (the operator-reported bug). Every drip calendar event (the materialized
// current step AND the synthetic follow-up steps) carries `dripId`, so both route there. A standalone
// campaign (no dripId) opens the same page by its OWN id — the page synthesizes it into a one-step
// sequence. (It used to open a row menu instead; the page is now where a campaign is viewed AND
// edited — messages, audience, send time — so every campaign click lands there.)
//
// Pure so the routing is unit-testable without a DOM.

export type CalendarEditTarget =
  // navigate to /campaigns/:id — the drip id for a sequence step, the campaign id for a standalone one
  { kind: 'sequence-detail'; campaignId: string };

export interface CalendarEventLike {
  _id?: string;
  isFollowupStep?: boolean;
  dripId?: string | null;
  dripStepIndex?: number;
  parentCampaignId?: string;
}

/**
 * Returns the edit target for a clicked calendar campaign event, or null when there's nothing to
 * open. ANY drip occurrence (it carries `dripId`) → open the DRIP id in SequenceDetail, so the full
 * sequence renders with per-step Edit. A standalone campaign → its own page, by its own id.
 *
 * `campaignId` here is the URL id for `/campaigns/:id`. For a drip it is the DRIP's id (which the
 * identity-resolving route renders as the whole sequence) — deliberately NOT the step campaign's own
 * `_id`, which would render only Message 1.
 */
export function resolveCalendarEditTarget(event: CalendarEventLike | null | undefined): CalendarEditTarget | null {
  if (!event) return null;
  // Any drip occurrence — the materialized current step or a synthetic follow-up step — opens the
  // parent DRIP in SequenceDetail (all steps). Route by dripId, never the step campaign's _id.
  if (event.dripId) {
    return { kind: 'sequence-detail', campaignId: String(event.dripId) };
  }
  // A plain standalone campaign → its own page.
  if (event._id) return { kind: 'sequence-detail', campaignId: String(event._id) };
  return null;
}
